import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, BookOpen, Check, CircleHelp, ExternalLink, FileText, Globe, Link2, Loader2, MessageSquareText, Pencil, Plus, RefreshCw, Search, Sparkles, Trash2, Upload, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { TabbedPage } from '@/components/TabbedPage';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useAuth } from '@/hooks/useAuth';
import { TabBody } from './agentShared';
import { knowledgeService, KnowledgeGap, KnowledgeHit, KnowledgeSource, KnowledgeSourceType, KnowledgeSummary } from '@/services/knowledge.service';

const TYPE_META: Record<KnowledgeSourceType, { label: string; icon: React.ReactNode; blurb: string }> = {
  website: { label: 'Whole website', icon: <Globe size={16} />, blurb: 'Reads your sitemap (or follows links) — up to 50 pages, re-read weekly.' },
  url: { label: 'One web page', icon: <Link2 size={16} />, blurb: 'A single page such as pricing, FAQ or delivery areas. Re-read weekly.' },
  file: { label: 'File', icon: <FileText size={16} />, blurb: 'PDF, Word (.docx), text or CSV up to 15 MB — menus, brochures, policies.' },
  text: { label: 'Paste text', icon: <MessageSquareText size={16} />, blurb: 'Anything you can type or paste — opening hours, policies, notes for the AI.' },
  faq: { label: 'Question & answer', icon: <CircleHelp size={16} />, blurb: 'One exact answer to a question customers ask.' },
};

export default function AiKnowledge() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const [summary, setSummary] = useState<KnowledgeSummary | null>(null);
  const [adding, setAdding] = useState<KnowledgeSourceType | 'pick' | null>(null);
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => { knowledgeService.summary().then(setSummary).catch(() => undefined); }, [version]);

  return (
    <>
      <TabbedPage
        title="Knowledge"
        breadcrumb="AI Agents / Knowledge"
        description="What your AI agents — and, if you switch it on, your WhatsApp AI — answer from."
        right={isAdmin ? <Button iconLeft={<Plus size={14} />} onClick={() => setAdding('pick')}>Add knowledge</Button> : undefined}
        tabs={[
          { id: 'sources', label: 'Sources', icon: <BookOpen size={14} />, render: () => <TabBody><SourcesTab summary={summary} isAdmin={isAdmin} version={version} onChanged={refresh} onAdd={() => setAdding('pick')} /></TabBody> },
          { id: 'gaps', label: `Unanswered${summary?.openGaps ? ` (${summary.openGaps})` : ''}`, icon: <CircleHelp size={14} />, render: () => <TabBody><GapsTab isAdmin={isAdmin} onChanged={refresh} /></TabBody> },
          { id: 'ask', label: 'Ask it', icon: <Search size={14} />, render: () => <TabBody><AskTab /></TabBody> },
        ]}
      />
      {adding && <AddModal initial={adding} onClose={() => setAdding(null)} onAdded={() => { setAdding(null); refresh(); }} />}
    </>
  );
}

// ─── Sources ──────────────────────────────────────────────────────────────

function SourcesTab({ summary, isAdmin, version, onChanged, onAdd }: { summary: KnowledgeSummary | null; isAdmin: boolean; version: number; onChanged: () => void; onAdd: () => void }) {
  const [rows, setRows] = useState<KnowledgeSource[] | null>(null);
  const [viewing, setViewing] = useState<KnowledgeSource | null>(null);
  const [editing, setEditing] = useState<KnowledgeSource | null>(null);
  const [deleting, setDeleting] = useState<KnowledgeSource | null>(null);

  const load = useCallback(() => knowledgeService.sources().then(setRows).catch(() => setRows((r) => r ?? [])), []);
  useEffect(() => { void load(); }, [load, version]);
  const working = rows?.some((r) => r.status === 'queued' || r.status === 'processing');
  // Indexing runs in the background — poll only while something is still working.
  useEffect(() => {
    if (!working) return;
    const t = setInterval(() => { void load(); }, 3000);
    return () => clearInterval(t);
  }, [working, load]);
  const wasWorking = useRef(false);
  useEffect(() => { if (wasWorking.current && !working) onChanged(); wasWorking.current = !!working; }, [working, onChanged]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast.success(ok); await load(); onChanged(); } catch (e: any) { if (!e?.response) toast.error('Something went wrong'); }
  };

  return (
    <div className="space-y-5">
      {summary && <SummaryStrip s={summary} />}
      {rows === null ? (
        <div className="space-y-3">{[1, 2, 3].map((n) => <div key={n} className="skeleton h-20 rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-border bg-surface px-6 py-14 text-center">
          <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary"><BookOpen size={24} /></span>
          <div className="font-display text-lg font-semibold text-text-primary">Teach your AI about your business</div>
          <p className="mt-1 max-w-md text-sm text-text-muted">Add your website, a menu or brochure, or a few questions and answers. The AI answers from them and tells you what it couldn't answer.</p>
          {isAdmin && <Button className="mt-5" iconLeft={<Plus size={14} />} onClick={onAdd}>Add knowledge</Button>}
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-surface">
          {rows.map((s) => (
            <div key={s.sourceUid} className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 last:border-0">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">{TYPE_META[s.type].icon}</span>
              <div className="min-w-[12rem] flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 truncate text-sm font-semibold text-text-primary">{s.title}</span>
                  <StatusPill s={s} />
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-text-muted">
                  <span>{TYPE_META[s.type].label}</span>
                  {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex max-w-[280px] items-center gap-1 truncate hover:text-primary"><ExternalLink size={11} />{s.url.replace(/^https?:\/\//, '')}</a>}
                  {s.file && <span className="truncate">{s.file.name}</span>}
                  {s.status === 'ready' && <span>{s.type === 'website' ? `${s.pageCount} pages · ` : ''}{s.chunkCount} section{s.chunkCount === 1 ? '' : 's'}</span>}
                  {s.lastSyncedAt && <span>Updated {formatDistanceToNow(new Date(s.lastSyncedAt), { addSuffix: true })}</span>}
                </div>
                {s.status === 'failed' && s.error && <p className="mt-1 text-xs text-danger">{s.error}</p>}
                {s.status === 'ready' && s.warning && <p className="mt-1 flex items-start gap-1 text-xs text-warning"><AlertTriangle size={12} className="mt-px shrink-0" />{s.warning}</p>}
              </div>
              <div className="ml-auto flex shrink-0 items-center gap-1">
                {s.status === 'ready' && <IconBtn label="See what the AI reads" onClick={() => setViewing(s)}><BookOpen size={14} /></IconBtn>}
                {isAdmin && (s.type === 'text' || s.type === 'faq') && <IconBtn label="Edit" onClick={() => setEditing(s)}><Pencil size={14} /></IconBtn>}
                {isAdmin && s.status !== 'processing' && s.status !== 'queued' && <IconBtn label={s.type === 'url' || s.type === 'website' ? 'Read again now' : 'Re-index'} onClick={() => act(() => knowledgeService.resync(s.sourceUid), 'Updating…')}><RefreshCw size={14} /></IconBtn>}
                {isAdmin && <IconBtn label="Delete" danger onClick={() => setDeleting(s)}><Trash2 size={14} /></IconBtn>}
              </div>
            </div>
          ))}
        </div>
      )}
      {viewing && <ChunksModal source={viewing} onClose={() => setViewing(null)} />}
      {editing && <EditModal source={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} />}
      <ConfirmDialog
        open={!!deleting}
        title={`Delete "${deleting?.title ?? ''}"?`}
        message="The AI stops using it straight away. Conversations that already cited it keep their history."
        danger
        onConfirm={() => { const d = deleting!; setDeleting(null); void act(() => knowledgeService.remove(d.sourceUid), 'Deleted'); }}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

function SummaryStrip({ s }: { s: KnowledgeSummary }) {
  const semantic = !!s.embeddingRoute;
  return (
    <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
      <Tile label="Sources" value={`${s.ready}/${s.sources}`} hint={s.working ? `${s.working} updating…` : s.failed ? `${s.failed} failed` : 'ready'} />
      <Tile label="Sections indexed" value={s.chunks.toLocaleString()} hint={`of ${s.limits.chunksPerCompany.toLocaleString()}`} />
      <Tile label="Unanswered questions" value={String(s.openGaps)} hint="asked by customers" tone={s.openGaps ? 'warning' : undefined} />
      <Tile
        label="Search"
        value={semantic ? 'Meaning + keywords' : 'Keywords only'}
        hint={semantic ? `${s.embeddingRoute!.provider} · ${s.embeddingRoute!.model}` : 'Add an OpenAI, Gemini, Qwen or Mistral key in Settings → AI'}
        tone={semantic && !s.keywordOnly ? undefined : 'warning'}
      />
    </div>
  );
}

function Tile({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'warning' }) {
  return (
    <div className="min-w-0 rounded-2xl border border-border bg-surface p-4">
      <div className="text-xs font-medium text-text-muted">{label}</div>
      <div className={clsx('mt-1 truncate text-lg font-semibold', tone === 'warning' ? 'text-warning' : 'text-text-primary')}>{value}</div>
      {hint && <div className="mt-0.5 truncate text-xs text-text-muted" title={hint}>{hint}</div>}
    </div>
  );
}

function StatusPill({ s }: { s: KnowledgeSource }) {
  if (s.status === 'queued' || s.status === 'processing') {
    return <span className="inline-flex items-center gap-1 rounded-full bg-info-soft px-2 py-0.5 text-[11px] font-medium text-info"><Loader2 size={11} className="animate-spin" />{s.status === 'queued' ? 'Waiting' : 'Reading'}</span>;
  }
  if (s.status === 'failed') return <span className="rounded-full bg-danger-soft px-2 py-0.5 text-[11px] font-medium text-danger">Failed</span>;
  return <span className={clsx('rounded-full px-2 py-0.5 text-[11px] font-medium', s.embedding ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning')}>{s.embedding ? 'Ready' : 'Ready · keywords only'}</span>;
}

function IconBtn({ label, onClick, danger, children }: { label: string; onClick: () => void; danger?: boolean; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} title={label} aria-label={label}
      className={clsx('rounded-lg p-2 text-text-muted transition-colors', danger ? 'hover:bg-danger-soft hover:text-danger' : 'hover:bg-surface-alt hover:text-text-primary')}>
      {children}
    </button>
  );
}

// ─── Add / edit / view ─────────────────────────────────────────────────────

function AddModal({ initial, onClose, onAdded }: { initial: KnowledgeSourceType | 'pick'; onClose: () => void; onAdded: () => void }) {
  const [type, setType] = useState<KnowledgeSourceType | null>(initial === 'pick' ? null : initial);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [question, setQuestion] = useState('');
  const [maxPages, setMaxPages] = useState(25);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (type === 'url') await knowledgeService.addUrl({ url: url.trim(), title: title.trim() || undefined });
      else if (type === 'website') await knowledgeService.addWebsite({ url: url.trim(), maxPages });
      else if (type === 'text') await knowledgeService.addText({ title: title.trim(), text });
      else if (type === 'faq') await knowledgeService.addFaq({ question: question.trim(), answer: text.trim() });
      else if (type === 'file' && file) await knowledgeService.addFile(file, title.trim() || undefined);
      toast.success('Added — the AI can use it in a moment');
      onAdded();
    } catch (e: any) {
      if (!e?.response) toast.error('Could not add it');
    } finally {
      setSaving(false);
    }
  };
  const ready = type === 'url' || type === 'website' ? /^https?:\/\/\S+\.\S+/.test(url.trim()) || /^[\w-]+(\.[\w-]+)+/.test(url.trim())
    : type === 'text' ? !!title.trim() && text.trim().length >= 20
      : type === 'faq' ? !!question.trim() && !!text.trim()
        : type === 'file' ? !!file : false;
  const withScheme = (u: string) => (/^https?:\/\//i.test(u.trim()) || !u.trim() ? u : `https://${u.trim()}`);

  return (
    <Modal open onClose={onClose} title={type ? `Add · ${TYPE_META[type].label}` : 'Add knowledge'} widthClass="max-w-xl">
      {!type ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {(Object.keys(TYPE_META) as KnowledgeSourceType[]).map((t) => (
            <button key={t} type="button" onClick={() => setType(t)} className="flex items-start gap-3 rounded-xl border border-border bg-surface p-3 text-left transition-colors hover:border-primary/50 hover:bg-primary-soft/40">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">{TYPE_META[t].icon}</span>
              <span className="min-w-0"><span className="block text-sm font-semibold text-text-primary">{TYPE_META[t].label}</span><span className="mt-0.5 block text-xs leading-snug text-text-muted">{TYPE_META[t].blurb}</span></span>
            </button>
          ))}
        </div>
      ) : (
        <div>
          <p className="mb-4 text-xs text-text-muted">{TYPE_META[type].blurb}</p>
          {(type === 'url' || type === 'website') && (
            <FormField label={type === 'website' ? 'Website address' : 'Page address'} required>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} onBlur={() => setUrl(withScheme(url))} placeholder={type === 'website' ? 'https://yourshop.com' : 'https://yourshop.com/faq'} autoFocus />
            </FormField>
          )}
          {type === 'website' && (
            <FormField label="Pages to read" hint="Max 50">
              <Input type="number" min={1} max={50} value={maxPages} onChange={(e) => setMaxPages(Math.min(50, Math.max(1, Number(e.target.value) || 1)))} />
            </FormField>
          )}
          {(type === 'url' || type === 'text' || type === 'file') && (
            <FormField label="Title" required={type === 'text'} hint={type === 'text' ? undefined : 'Optional'}>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={type === 'text' ? 'e.g. Delivery & refunds' : ''} maxLength={200} />
            </FormField>
          )}
          {type === 'faq' && (
            <FormField label="Question" required>
              <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Do you deliver on Fridays?" maxLength={500} autoFocus />
            </FormField>
          )}
          {(type === 'text' || type === 'faq') && (
            <FormField label={type === 'faq' ? 'Answer' : 'Text'} required>
              <Textarea rows={type === 'faq' ? 4 : 10} value={text} onChange={(e) => setText(e.target.value)} placeholder={type === 'faq' ? 'Yes — we deliver every day from 9am to 10pm.' : 'Paste your policies, opening hours, product details…'} />
            </FormField>
          )}
          {type === 'file' && (
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border bg-surface-alt/40 px-6 py-8 text-center transition-colors hover:border-primary">
              <Upload size={20} className="text-primary" />
              <span className="text-sm font-semibold text-text-primary">{file ? file.name : 'Choose a file'}</span>
              <span className="text-xs text-text-muted">{file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : 'PDF, .docx, .txt, .md or .csv — up to 15 MB'}</span>
              <input type="file" className="hidden" accept=".pdf,.docx,.txt,.md,.csv,application/pdf,text/plain" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
          )}
          <div className="mt-5 flex justify-between gap-2">
            {initial === 'pick' ? <Button variant="ghost" onClick={() => setType(null)}>Back</Button> : <span />}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button loading={saving} disabled={!ready} onClick={save}>Add</Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function EditModal({ source, onClose, onSaved }: { source: KnowledgeSource; onClose: () => void; onSaved: () => void }) {
  const [full, setFull] = useState<KnowledgeSource | null>(null);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    knowledgeService.source(source.sourceUid).then((s) => { setFull(s); setTitle(s.type === 'faq' ? s.question ?? s.title : s.title); setText(s.text ?? ''); }).catch(onClose);
  }, [source.sourceUid, onClose]);
  const save = async () => {
    setSaving(true);
    try {
      await knowledgeService.update(source.sourceUid, source.type === 'faq' ? { question: title, answer: text } : { title, text });
      toast.success('Saved — re-indexing');
      onSaved();
    } catch (e: any) { if (!e?.response) toast.error('Could not save'); } finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title={source.type === 'faq' ? 'Edit question & answer' : 'Edit text'} widthClass="max-w-xl">
      {!full ? <div className="skeleton h-40 rounded-xl" /> : (
        <div>
          <FormField label={source.type === 'faq' ? 'Question' : 'Title'}><Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={source.type === 'faq' ? 500 : 200} /></FormField>
          <FormField label={source.type === 'faq' ? 'Answer' : 'Text'}><Textarea rows={source.type === 'faq' ? 4 : 12} value={text} onChange={(e) => setText(e.target.value)} /></FormField>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={saving} disabled={!title.trim() || !text.trim()} onClick={save}>Save</Button></div>
        </div>
      )}
    </Modal>
  );
}

function ChunksModal({ source, onClose }: { source: KnowledgeSource; onClose: () => void }) {
  const [rows, setRows] = useState<Array<{ idx: number; title: string; url?: string; text: string }> | null>(null);
  useEffect(() => { knowledgeService.chunks(source.sourceUid).then(setRows).catch(() => setRows([])); }, [source.sourceUid]);
  return (
    <Modal open onClose={onClose} title={source.title} widthClass="max-w-2xl">
      <p className="mb-3 text-xs text-text-muted">This is the text the AI searches — split into {source.chunkCount} section{source.chunkCount === 1 ? '' : 's'}. If something important is missing, add it as text or a Q&amp;A.</p>
      <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
        {rows === null ? <div className="skeleton h-32 rounded-xl" /> : rows.map((c) => (
          <div key={c.idx} className="rounded-xl border border-border bg-surface-alt/50 p-3">
            <div className="mb-1 flex items-center gap-2 text-[11px] font-medium text-text-muted"><span>#{c.idx + 1}</span><span className="truncate">{c.title}</span>{c.url && <a href={c.url} target="_blank" rel="noreferrer" className="hover:text-primary"><ExternalLink size={11} /></a>}</div>
            <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-text-secondary">{c.text}</p>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ─── Unanswered questions ──────────────────────────────────────────────────

function GapsTab({ isAdmin, onChanged }: { isAdmin: boolean; onChanged: () => void }) {
  const [status, setStatus] = useState<KnowledgeGap['status']>('open');
  const [rows, setRows] = useState<KnowledgeGap[] | null>(null);
  const [answering, setAnswering] = useState<string | null>(null);
  const [answer, setAnswer] = useState('');
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => knowledgeService.gaps(status).then(setRows).catch(() => setRows([])), [status]);
  useEffect(() => { setRows(null); void load(); }, [load]);

  const submit = async (g: KnowledgeGap) => {
    if (!answer.trim()) return;
    setSaving(true);
    try {
      await knowledgeService.answerGap(g.gapUid, answer.trim());
      toast.success('Answer saved — the AI uses it from now on');
      setAnswering(null); setAnswer('');
      await load(); onChanged();
    } catch (e: any) { if (!e?.response) toast.error('Could not save'); } finally { setSaving(false); }
  };
  const setGap = async (g: KnowledgeGap, next: 'open' | 'dismissed') => {
    try { await knowledgeService.setGapStatus(g.gapUid, next); await load(); onChanged(); } catch (e: any) { if (!e?.response) toast.error('Something went wrong'); }
  };

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-text-muted">Questions customers asked that the AI couldn't answer from what it knows. Answer once — it becomes a Q&amp;A the AI uses from then on.</p>
        <div className="flex rounded-xl bg-surface-alt p-1">
          {(['open', 'answered', 'dismissed'] as const).map((s) => (
            <button key={s} type="button" onClick={() => setStatus(s)} className={clsx('rounded-lg px-3 py-1.5 text-xs font-medium capitalize', status === s ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary')}>{s}</button>
          ))}
        </div>
      </div>
      {rows === null ? <div className="skeleton h-40 rounded-2xl" /> : rows.length === 0 ? (
        <Card><div className="flex items-center gap-3 text-sm text-text-muted"><Check size={16} className="text-success" />{status === 'open' ? 'Nothing waiting — the AI had an answer for everything customers asked.' : `No ${status} questions.`}</div></Card>
      ) : rows.map((g) => (
        <Card key={g.gapUid}>
          <div className="flex flex-wrap items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-text-primary">{g.question}</div>
              <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-text-muted">
                <span>Asked {g.count}×</span>
                {g.lastAskedAt && <span>last {formatDistanceToNow(new Date(g.lastAskedAt), { addSuffix: true })}</span>}
                {g.channel && <span>{g.channel === 'api' ? 'App' : g.channel === 'web' ? 'Website' : 'WhatsApp'}</span>}
              </div>
              {g.examples.length > 1 && <ul className="mt-2 space-y-0.5 text-xs text-text-muted">{g.examples.filter((e) => e !== g.question).slice(-3).map((e) => <li key={e}>“{e}”</li>)}</ul>}
            </div>
            {isAdmin && status === 'open' && answering !== g.gapUid && (
              <div className="flex gap-2">
                <Button size="sm" iconLeft={<Sparkles size={13} />} onClick={() => { setAnswering(g.gapUid); setAnswer(''); }}>Answer</Button>
                <Button size="sm" variant="ghost" iconLeft={<X size={13} />} onClick={() => setGap(g, 'dismissed')}>Dismiss</Button>
              </div>
            )}
            {isAdmin && status === 'dismissed' && <Button size="sm" variant="ghost" onClick={() => setGap(g, 'open')}>Reopen</Button>}
          </div>
          {answering === g.gapUid && (
            <div className="mt-3 space-y-2">
              <Textarea rows={3} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Write the answer the AI should give…" autoFocus />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAnswering(null)}>Cancel</Button>
                <Button size="sm" loading={saving} disabled={!answer.trim()} onClick={() => submit(g)}>Save answer</Button>
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

// ─── Ask it ───────────────────────────────────────────────────────────────

function AskTab() {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState<KnowledgeHit[] | null>(null);
  const run = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try { setHits(await knowledgeService.search(q.trim())); } catch (e: any) { if (!e?.response) toast.error('Search failed'); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-4xl space-y-4">
      <Card>
        <p className="mb-3 text-sm text-text-muted">Type a question the way a customer would. You'll see exactly which sections the AI would be given to answer it.</p>
        <div className="flex gap-2">
          <div className="min-w-0 flex-1"><Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void run(); }} placeholder="Do you deliver to Dubai Marina on Fridays?" iconLeft={<Search size={14} />} /></div>
          <Button loading={busy} disabled={!q.trim()} onClick={run}>Search</Button>
        </div>
      </Card>
      {hits !== null && (hits.length === 0 ? (
        <Card><div className="flex items-start gap-2 text-sm text-text-muted"><AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning" />Nothing in your knowledge base matches. The AI would say it'll check with the team — and the question would show up under Unanswered.</div></Card>
      ) : hits.map((h) => (
        <Card key={`${h.sourceUid}-${h.n}`}>
          <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-primary-soft px-2 py-0.5 font-semibold text-primary">#{h.n}</span>
            <span className="font-medium text-text-primary">{h.title}</span>
            {h.url && <a href={h.url} target="_blank" rel="noreferrer" className="text-text-muted hover:text-primary"><ExternalLink size={12} /></a>}
            <span className="ml-auto text-text-muted">match {Math.round(h.score * 100)}%</span>
          </div>
          <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-text-secondary">{h.text}</p>
        </Card>
      )))}
    </div>
  );
}
